// ✅ src/pages/VehicleV2/SpareLifeDetailReport_Print.jsx
// React PRINT page for: SpareLifeDetailReportPrint.aspx (Crystal print)
// Old ASPX: uses Session["SpareLifeDetail"] -> dt -> VehicleSpareLifeDetailReport.rpt
// New React: calls backend API and prints in same template style as VehiclewiseReport_Print.jsx

import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaPrint } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

// ✅ Change this to your real backend route
// Example backend should return: { success:true, rows:[...] }
const API_BASE = "https://nissi-india.com/api/spareLifeDetail";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();
const isSelect = (v) => {
  const s = toStr(v);
  return !s || s.toLowerCase() === "select";
};
const clean = (v) => (isSelect(v) ? "" : toStr(v));

const safeDate = (v) => toStr(v);

const pick = (obj, keys, fallback = "") => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && toStr(v) !== "") return v;
  }
  return fallback;
};

const normalizeKey = (k) =>
  toStr(k)
    .replaceAll("_", " ")
    .replaceAll("-", " ")
    .replace(/\s+/g, " ")
    .trim();

const formatCell = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return s;
};

export default function SpareLifeDetailReport_Print() {
  const navigate = useNavigate();
  const location = useLocation();

  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  // ✅ support BOTH state + querystring
  const qs = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const passed = location?.state || {};

  // ✅ Build filters from URL first, then state fallback
  const filters = useMemo(() => {
    // These keys are a guess based on your earlier SpareLife listing flow.
    // Keep them aligned with whatever you pass from listing page.
    const q = {
      Sparevehicleid: toStr(qs.get("Sparevehicleid")),
      Sparevehiclecode: toStr(qs.get("Sparevehiclecode")),
      Sparevehicledetid: toStr(qs.get("Sparevehicledetid")),
      Sparevehiclebranchid: toStr(qs.get("Sparevehiclebranchid")),
      Sparevehicleregno: toStr(qs.get("Sparevehicleregno")),
      Sparebranchid: toStr(qs.get("Sparebranchid")),
      branch: toStr(qs.get("branch")),
      vehicleCode: toStr(qs.get("vehicleCode")),
      vehicleName: toStr(qs.get("vehicleName")),
      vehRegNo: toStr(qs.get("vehRegNo")),
      fromDate: safeDate(qs.get("fromDate")),
      toDate: safeDate(qs.get("toDate")),
      itemName: toStr(qs.get("itemName")),
      spareName: toStr(qs.get("spareName")),
    };

    return {
      // vehicle context
      vehicleId: clean(q.Sparevehicleid) || clean(passed?.Sparevehicleid ?? passed?.vehicleId ?? passed?.mainVehicleId),
      vehicleCode: clean(q.Sparevehiclecode) || clean(q.vehicleCode) || clean(passed?.Sparevehiclecode ?? passed?.vehicleCode),
      vehicleDetId: clean(q.Sparevehicledetid) || clean(passed?.Sparevehicledetid ?? passed?.vehicleDetId),
      vehicleBranchId: clean(q.Sparevehiclebranchid) || clean(passed?.Sparevehiclebranchid ?? passed?.vehicleBranchId),
      vehRegNo: clean(q.Sparevehicleregno) || clean(q.vehRegNo) || clean(passed?.Sparevehicleregno ?? passed?.vehRegNo ?? passed?.regNo),

      // filters
      branchId: clean(q.Sparebranchid) || clean(q.branch) || clean(passed?.Sparebranchid ?? passed?.branchId ?? passed?.branch),
      fromDate: safeDate(q.fromDate) || safeDate(passed?.fromDate ?? passed?.fromdate),
      toDate: safeDate(q.toDate) || safeDate(passed?.toDate ?? passed?.todate),
      itemName: clean(q.itemName) || clean(q.spareName) || clean(passed?.itemName ?? passed?.spareName),

      // auth context (optional)
      loginuserid: Number(sessionStorage.getItem("loginuserid")) || Number(passed?.loginuserid) || 0,
      branchType: toStr(sessionStorage.getItem("branchtype") || passed?.branchType || ""),
    };
  }, [qs, passed]);

  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [rows, setRows] = useState([]);

  const hasAnyParam = useMemo(() => {
    return (
      !!toStr(filters.vehicleId) ||
      !!toStr(filters.vehicleCode) ||
      !!toStr(filters.vehRegNo) ||
      !!toStr(filters.branchId) ||
      !!toStr(filters.fromDate) ||
      !!toStr(filters.toDate) ||
      !!toStr(filters.itemName)
    );
  }, [filters]);

  const load = async () => {
    try {
      setLoading(true);
      setErrMsg("");

      if (!hasAnyParam) {
        throw new Error("Print params missing. Open print from Spare Life Detail Report page.");
      }

      // ✅ choose endpoint name you prefer:
      //   GET /api/spareLifeDetail/print
      // or GET /api/spareLifeDetail/list
      const { data } = await axios.get(`${API_BASE}/print`, { params: filters });

      if (!data?.success) throw new Error(data?.message || "Failed to load report");

      setRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to load report";
      setErrMsg(msg);
      setRows([]);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  /* ---------------- dynamic columns ---------------- */
  const columns = useMemo(() => {
    const r0 = rows?.[0];
    if (!r0 || typeof r0 !== "object") return [];

    const keys = Object.keys(r0);

    // remove noisy/internal fields if any
    const blocked = new Set([
      "id",
      "RowId",
      "rowid",
      "fld_id",
      "total",
      "Total",
      "sno",
      "SNo",
    ]);

    return keys.filter((k) => !blocked.has(k));
  }, [rows]);

  return (
    <div className={`${isDark ? "bg-gray-900" : "bg-gray-100"} min-h-screen`}>
      <div className="w-full bg-white">
        <style>{`
          @page { size: A4 portrait; margin: 10mm; }
          @media print {
            body { background: #fff !important; }
            .no-print { display: none !important; }
            .print-area { margin: 0 !important; padding: 0 !important; }
          }
        `}</style>

        {/* Top controls */}
        <div className="no-print flex items-center justify-between px-4 py-3 border-b bg-white">
          <div className="text-sm">
            {loading ? "Loading..." : errMsg ? <span className="text-red-600">{errMsg}</span> : `Records: ${rows.length}`}
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-50 flex items-center gap-2"
            >
              <FaArrowLeft /> Back
            </button>

            <button
              onClick={() => window.print()}
              disabled={loading}
              className={`bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700 text-white px-4 py-2 rounded flex items-center gap-2 disabled:opacity-60`}
            >
              <FaPrint /> Print
            </button>
          </div>
        </div>

        {/* Print content */}
        <div className="print-area mx-auto max-w-[820px] p-4 print:p-0">
          <div className="border-2 border-black p-3">
            <div className="border border-black">
              {/* Header */}
              <div className="border-b border-black p-2">
                <div className="flex items-center gap-3">
                  <div className="w-[110px] h-[80px] border border-black flex items-center justify-center">
                    <div className="text-[10px] font-bold text-center leading-3">
                      UNDER
                      <br />
                      CONSTRUCTION
                    </div>
                  </div>

                  <div className="flex-1 text-center leading-5">
                    <div className="font-bold text-lg">Nissi Infotech</div>
                    <div className="font-semibold text-sm">Nissi</div>
                    <div className="font-semibold text-sm">CIVIL CONSTRUCTION</div>
                    <div className="text-[11px] mt-1">
                      3/42-12, Nagappa colony, kavundampalayam-641030, Tamil Nadu, India.
                    </div>
                    <div className="text-[11px]">
                      Phone No: 9003744644&nbsp;&nbsp;&nbsp;&nbsp; infracnsoft-implementation@nissiinfotech.in
                    </div>
                  </div>
                </div>
              </div>

              {/* Title */}
              <div className="border-b border-black text-center font-bold py-2 text-sm">
                SPARE LIFE DETAIL REPORT
              </div>

              {/* Filter info */}
              <div className="border-b border-black p-2 text-[12px]">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div>
                      <span className="font-semibold">Branch</span> : {filters.branchId || "-"}
                    </div>
                    <div>
                      <span className="font-semibold">Spare / Item</span> : {filters.itemName || "-"}
                    </div>
                    <div>
                      <span className="font-semibold">Vehicle</span> : {filters.vehicleCode || "-"}
                    </div>
                    <div>
                      <span className="font-semibold">Reg No</span> : {filters.vehRegNo || "-"}
                    </div>
                  </div>

                  <div className="text-right">
                    <div>
                      <span className="font-semibold">From</span> : {filters.fromDate || "-"}
                    </div>
                    <div>
                      <span className="font-semibold">To</span> : {filters.toDate || "-"}
                    </div>
                    <div>
                      <span className="font-semibold">Vehicle ID</span> : {filters.vehicleId || "-"}
                    </div>
                    <div>
                      <span className="font-semibold">Branch Type</span> : {filters.branchType || "-"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="p-2">
                <table className="w-full border border-black border-collapse text-[12px]">
                  <thead>
                    <tr>
                      <th className="border border-black px-2 py-1 w-[6%]">S.No</th>
                      {columns.length === 0 ? (
                        <th className="border border-black px-2 py-1">Details</th>
                      ) : (
                        columns.map((c) => (
                          <th key={c} className="border border-black px-2 py-1">
                            {normalizeKey(c)}
                          </th>
                        ))
                      )}
                    </tr>
                  </thead>

                  <tbody>
                    {!loading && rows.length === 0 ? (
                      <tr>
                        <td className="border border-black px-2 py-2 text-center" colSpan={Math.max(2, columns.length + 1)}>
                          No records found.
                        </td>
                      </tr>
                    ) : (
                      rows.map((r, idx) => (
                        <tr key={pick(r, ["id", "fld_id", "RowId"], `${idx}`)}>
                          <td className="border border-black px-2 py-1 text-center">{idx + 1}</td>

                          {columns.length === 0 ? (
                            <td className="border border-black px-2 py-1">
                              <pre className="whitespace-pre-wrap break-words">{JSON.stringify(r, null, 2)}</pre>
                            </td>
                          ) : (
                            columns.map((c) => (
                              <td key={c} className="border border-black px-2 py-1">
                                {formatCell(r?.[c]) || "-"}
                              </td>
                            ))
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="border-t border-black p-2">
                <div className="border border-black p-2 text-[11px]">
                  Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com
                </div>
              </div>
            </div>
          </div>

          {/* Debug */}
          <div className="no-print mt-4 text-xs text-gray-600">
            <details>
              <summary className="cursor-pointer">Debug: Query + Filters</summary>
              <pre className="mt-2 p-3 border rounded bg-gray-50 overflow-auto">
                {JSON.stringify(
                  {
                    urlSearch: location.search,
                    filters,
                    rowsCount: rows.length,
                    columns,
                  },
                  null,
                  2
                )}
              </pre>
            </details>
          </div>
        </div>

        <ToastContainer position="top-right" autoClose={2500} theme={isDark ? "dark" : "light"} />
      </div>
    </div>
  );
}